import { SiteLayout } from "@/components/SiteLayout";
import { TALLY_CONTACT_PUBLIC_URL } from "@/components/templates/BaseTemplateTallyContact";
import { ConsultaCtaButton } from "@/components/templates/ConsultaCtaButton";
import {
  parsePortfolioUrl,
  portfolioSectionAnchorId,
} from "@/config/portfolioConfig";
import { useImageProtection } from "@/hooks/useImageProtection";
import { usePhotoImpressions } from "@/hooks/usePhotoImpressions";
import { usePortfolioSections } from "@/hooks/usePortfolioSections";
import { useSEO } from "@/hooks/useSEO";
import { useTemplateSelection } from "@/hooks/useTemplateSelection";
import { trackBaseTemplateClick } from "@/lib/baseTemplateAnalytics";
import { trackPhotoOpen } from "@/lib/photoTracking";
import { useTranslation } from "react-i18next";
import {
  getCloudinaryUrl,
  isCloudinaryImage,
  resolveLogoUrl,
  resolvePortfolioImageUrl,
} from "@/lib/cloudinaryUtils";
import {
  applyInstagramBrowserCSS,
  getInstagramOptimizations,
  isMobileDevice,
  trackInstagramBrowserUsage,
} from "@/lib/instagramBrowserDetection";
import { cn } from "@/lib/utils";
import { ChevronLeft, ChevronRight, Loader2, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

function portfolioStylesCss(isDark: boolean, isInstagram = false): string {
  const itemBg = isDark ? "#171717" : "#f5f5f5";
  const beforeBg = isDark
    ? "rgba(255, 255, 255, 0.06)"
    : "rgba(0, 0, 0, 0.05)";
  const activeShadow = isDark
    ? "0 4px 12px rgba(0, 0, 0, 0.45)"
    : "0 4px 12px rgba(0, 0, 0, 0.15)";
  const hoverShadow = isDark
    ? "0 8px 16px rgba(0, 0, 0, 0.5)"
    : "0 8px 16px rgba(0, 0, 0, 0.1)";

  return `
  .portfolio-grid {
    display: grid;
    /* Mobile/IG excellence: 2 cols dense on phone, consistent gaps that match side padding for clean even look */
    grid-template-columns: repeat(2, 1fr);
    gap: 6px;
    width: 100%;
    max-width: 1400px;
    margin: 0 auto;
    padding: 0 6px;
    padding-left: max(6px, env(safe-area-inset-left, 0px));
    padding-right: max(6px, env(safe-area-inset-right, 0px));
  }
  
  .portfolio-item {
    position: relative;
    overflow: hidden;
    background: ${itemBg};
    aspect-ratio: 4 / 3;
    transition: opacity 0.15s ease, transform 0.15s ease, box-shadow 0.15s ease;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
    border-radius: 3px;
    /* Extra touch optimization for IG in-app */
    touch-action: manipulation;
  }
  
  .portfolio-item::before {
    content: '';
    position: absolute;
    inset: 0;
    background: ${beforeBg};
    opacity: 0;
    transition: opacity 0.2s ease;
    z-index: 1;
    pointer-events: none;
  }
  
  /* Fotos horizontales: ocupan full row en mobile para apreciar formato, 2 cols en desktop */
  .portfolio-item.portfolio-item-wide {
    aspect-ratio: 16 / 9;
    grid-column: span 2;
  }
  
  .portfolio-item img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center center;
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    image-rendering: -webkit-optimize-contrast;
    transform: translateZ(0);
  }
  
  /* Mobile / IG excellence touch feedback - larger hit area, instant response */
  .portfolio-item:active {
    opacity: 0.92;
    transform: scale(0.985);
    box-shadow: ${activeShadow};
  }
  
  .portfolio-item:active::before {
    opacity: 1;
  }
  
  .portfolio-item button {
    min-height: 48px; /* iOS touch target excellence */
  }

  /* Horizontal section nav excellence on mobile */
  nav[aria-label*="Secciones"]::-webkit-scrollbar {
    display: none;
  }
  nav[aria-label*="Secciones"] {
    scrollbar-width: none;
  }
  
  @media (min-width: 480px) {
    .portfolio-grid {
      gap: 8px;
      padding: 0 8px;
    }
  }

  @media (min-width: 640px) {
    .portfolio-grid {
      grid-template-columns: repeat(2, 1fr);
      gap: 10px;
      padding: 0 12px;
    }
    .portfolio-item.portfolio-item-wide {
      grid-column: span 2;
    }
  }
  
  @media (min-width: 1024px) {
    .portfolio-grid {
      grid-template-columns: repeat(3, 1fr);
      gap: 12px;
      padding: 0 16px;
    }
    .portfolio-item.portfolio-item-wide {
      grid-column: span 2;
    }
  }
  
  @media (min-width: 1280px) {
    .portfolio-grid {
      gap: 14px;
      padding: 0 24px;
    }
  }
  
  /* Desktop hover effect */
  @media (hover: hover) and (pointer: fine) {
    .portfolio-item:hover {
      transform: translateY(-2px);
      box-shadow: ${hoverShadow};
    }
    
    .portfolio-item:hover img {
      transform: translateZ(0) scale(1.05);
    }
  }
  
  /* Safari/iOS + Instagram browser excellence viewport */
  .portfolio-lightbox-viewport {
    height: 100vh;
    min-height: -webkit-fill-available;
    height: 100dvh;
  }
  .portfolio-lightbox-viewport .portfolio-touch-area {
    height: 100vh;
    min-height: -webkit-fill-available;
    height: 100dvh;
  }
  
  /* Lightbox navigation arrows - Excellence for mobile + Instagram browser */
  .portfolio-nav-button {
    min-width: 48px !important;
    min-height: 48px !important;
    padding: 12px !important;
    transition: all 0.15s cubic-bezier(0.4, 0, 0.2, 1);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
    border: 1px solid rgba(255,255,255,0.15) !important;
  }
  
  .portfolio-nav-button:active {
    transform: scale(0.9) translateY(-50%);
    background: rgba(255, 255, 255, 0.25) !important;
  }
  
  @media (hover: hover) and (pointer: fine) {
    .portfolio-nav-button:hover {
      background: rgba(255, 255, 255, 0.2) !important;
      transform: scale(1.05) translateY(-50%);
    }
  }
  
  /* Close button enhanced for IG/mobile */
  .portfolio-close-button {
    min-width: 48px !important;
    min-height: 48px !important;
    padding: 10px !important;
    transition: all 0.15s cubic-bezier(0.4, 0, 0.2, 1);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
  }
  
  .portfolio-close-button:active {
    transform: scale(0.88);
    background: rgba(255, 255, 255, 0.25) !important;
  }
  
  @media (hover: hover) and (pointer: fine) {
    .portfolio-close-button:hover {
      background: rgba(255, 255, 255, 0.2) !important;
      transform: scale(1.08);
    }
  }
  
  /* Lightbox fade in/out */
  .portfolio-lightbox-viewport {
    animation: portfolioLightboxFadeIn 0.25s ease-out;
  }
  
  @keyframes portfolioLightboxFadeIn {
    from {
      opacity: 0;
      background-color: rgba(0, 0, 0, 0);
    }
    to {
      opacity: 1;
      background-color: rgba(0, 0, 0, 0.98);
    }
  }

  ${
    isInstagram
      ? `
  .instagram-browser-gallery .portfolio-item {
    transition: opacity 0.15s ease !important;
  }
  .instagram-browser-gallery .portfolio-item img {
    transition: none !important;
  }
  .instagram-browser-gallery .portfolio-item:active {
    transform: scale(0.99) translateZ(0);
  }
  `
      : ""
  }
`;
}

const Portfolio = () => {
  const { t } = useTranslation();
  useSEO("portfolio");
  useImageProtection();
  const { isBaseTemplate: isDarkShell, country } = useTemplateSelection();
  const instagramOpts = useMemo(() => getInstagramOptimizations(), []);
  const isInstagram = instagramOpts.isInstagramBrowser;
  const layoutRootClass = cn(
    isInstagram ? instagramOpts.cssClasses.container : undefined,
    isInstagram ? "instagram-browser-optimized instagram-browser-gallery" : undefined,
  );
  const portfolioCss = useMemo(
    () => portfolioStylesCss(isDarkShell, isInstagram),
    [isDarkShell, isInstagram],
  );
  const { sections, isLoading, portfolioPublicEnabled } = usePortfolioSections();
  const [selectedSection, setSelectedSection] = useState<number | null>(null);
  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const [failedUrls, setFailedUrls] = useState<Set<string>>(new Set());
  const [imageLoading, setImageLoading] = useState(true);
  const swipeStartRef = useRef<{ x: number; y: number; time: number } | null>(
    null,
  );
  const touchContainerRef = useRef<HTMLDivElement>(null);
  const lightboxOpenTimeRef = useRef<number | null>(null);
  const lastFocusedElementRef = useRef<HTMLElement | null>(null);

  // Impresiones del grid + opens → /admin/fotos-analytics (país/geo Worker).
  usePhotoImpressions({
    section: "portfolio",
    templateType: "Portfolio",
    countryCode: country || undefined,
    ready: sections,
  });

  useEffect(() => {
    const style = document.createElement("style");
    style.setAttribute("data-portfolio-theme", "");
    style.textContent = portfolioCss;
    document.head.appendChild(style);
    return () => {
      if (document.head.contains(style)) document.head.removeChild(style);
    };
  }, [portfolioCss]);

  // Instagram IAB: estilos globales + body class (mismo patrón que BioLinkTemplate)
  useEffect(() => {
    if (!isInstagram) return;
    applyInstagramBrowserCSS();
    document.body.classList.add("instagram-browser-optimized");
    trackInstagramBrowserUsage("Portfolio", country || "UNKNOWN");
    return () => {
      document.body.classList.remove("instagram-browser-optimized");
    };
  }, [isInstagram, country]);

  // /portfolio#desfile — scroll a la sección al cargar o cambiar el hash
  useEffect(() => {
    if (isLoading || sections.length === 0) return;

    const scrollToHash = () => {
      const hash = decodeURIComponent(window.location.hash.replace(/^#/, ""));
      if (!hash) return;
      const el = document.getElementById(hash);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    };

    const t = window.setTimeout(scrollToHash, 80);
    window.addEventListener("hashchange", scrollToHash);
    return () => {
      window.clearTimeout(t);
      window.removeEventListener("hashchange", scrollToHash);
    };
  }, [isLoading, sections]);

  // Safari/iOS: touch events con passive: false para que preventDefault funcione
  const handlersRef = useRef<{
    navigatePrev: () => void;
    navigateNext: () => void;
    closeLightbox: () => void;
    instagramOpts: ReturnType<typeof getInstagramOptimizations>;
  } | null>(null);

  const selectedSectionData =
    selectedSection !== null ? sections[selectedSection] : null;
  const currentEntries = selectedSectionData
    ? selectedSectionData.urls
        .map((raw) => parsePortfolioUrl(raw.trim()))
        .filter(
          (e) =>
            e.url &&
            isCloudinaryImage(e.url) &&
            !failedUrls.has(e.url),
        )
    : [];
  const currentUrls = currentEntries.map((e) => e.url);
  const currentIndex = Math.min(
    selectedIndex,
    Math.max(0, currentUrls.length - 1),
  );
  const currentUrl = currentUrls[currentIndex] ?? null;

  const navigatePrev = useCallback(() => {
    if (selectedSection === null || currentUrls.length === 0) return;
    setImageLoading(true);
    setSelectedIndex((i) => {
      const next = i > 0 ? i - 1 : currentUrls.length - 1;
      const src = currentUrls[next];
      if (src) {
        trackPhotoOpen(src, next, {
          section: "portfolio",
          templateType: "Portfolio",
          countryCode: country || undefined,
          openSource: "lightbox_swipe",
        });
      }
      return next;
    });
  }, [selectedSection, currentUrls, country]);

  const navigateNext = useCallback(() => {
    if (selectedSection === null || currentUrls.length === 0) return;
    setImageLoading(true);
    setSelectedIndex((i) => {
      const next = i < currentUrls.length - 1 ? i + 1 : 0;
      const src = currentUrls[next];
      if (src) {
        trackPhotoOpen(src, next, {
          section: "portfolio",
          templateType: "Portfolio",
          countryCode: country || undefined,
          openSource: "lightbox_swipe",
        });
      }
      return next;
    });
  }, [selectedSection, currentUrls, country]);

  const openLightbox = useCallback((sectionIdx: number, idx: number) => {
    // Excellence: remember trigger for focus restore on close
    lastFocusedElementRef.current = document.activeElement as HTMLElement | null;

    setImageLoading(true);
    setSelectedSection(sectionIdx);
    setSelectedIndex(idx);
    lightboxOpenTimeRef.current = Date.now();

    // Track engagement (excellence analytics)
    const sec = sections[sectionIdx];
    if (sec) {
      const entry = parsePortfolioUrl((sec.urls[idx] || "").trim());
      const src = entry.url || sec.urls[idx];
      if (src) {
        trackPhotoOpen(src, idx, {
          section: "portfolio",
          templateType: "Portfolio",
          countryCode: country || undefined,
          openSource: "portfolio",
        });
      }
      try {
        window.posthog?.capture?.("portfolio_lightbox_opened", {
          section_title: sec.title,
          image_index: idx,
          total_in_section: sec.urls.length,
          country_code: country || undefined,
        });
      } catch {
        /* no-op: analytics optional */
      }
    }
  }, [sections, country]);

  const closeLightbox = useCallback(() => {
    setSelectedSection(null);
    setSelectedIndex(0);
    setImageLoading(true);
    lightboxOpenTimeRef.current = null;

    // Excellence: restore focus to the thumbnail that opened the lightbox (a11y)
    const toFocus = lastFocusedElementRef.current;
    if (toFocus && typeof toFocus.focus === "function") {
      // Defer to ensure DOM unmount of lightbox doesn't steal focus
      window.setTimeout(() => {
        try { toFocus.focus(); } catch {
          /* no-op */
        }
      }, 0);
    }
    lastFocusedElementRef.current = null;
  }, []);

  handlersRef.current = {
    navigatePrev,
    navigateNext,
    closeLightbox,
    instagramOpts,
  };

  // Solo imágenes Cloudinary (con watermark). public_id corto → anteponer carpeta de sección.
  const resolveUrl = useCallback(
    (u: string, sectionTitle?: string) =>
      resolvePortfolioImageUrl(u, sectionTitle) ?? resolveLogoUrl(u) ?? u,
    []
  );

  const getOptimizedSrc = useCallback(
    (url: string, sectionTitle?: string, width?: number) => {
      if (!isCloudinaryImage(url)) return url;
      return getCloudinaryUrl(resolveUrl(url, sectionTitle), {
        width,
        watermark: true,
      });
    },
    [resolveUrl]
  );

  // Block body scroll when lightbox open (igual que Mi Galería en BaseTemplate)
  useEffect(() => {
    if (selectedSection !== null) {
      const originalOverflow = document.body.style.overflow;
      const originalPosition = document.body.style.position;
      const originalWidth = document.body.style.width;
      const originalHeight = document.body.style.height;

      if (instagramOpts.isInstagramBrowser || isMobileDevice()) {
        document.body.style.position = "fixed";
        document.body.style.width = "100%";
        document.body.style.height = "100%";
      }
      document.body.style.overflow = "hidden";

      return () => {
        document.body.style.overflow = originalOverflow;
        if (instagramOpts.isInstagramBrowser || isMobileDevice()) {
          document.body.style.position = originalPosition;
          document.body.style.width = originalWidth;
          document.body.style.height = originalHeight;
        }
      };
    }
  }, [selectedSection, instagramOpts.isInstagramBrowser]);

  // Keyboard navigation
  useEffect(() => {
    if (selectedSection === null) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        closeLightbox();
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        navigatePrev();
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        navigateNext();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedSection, navigatePrev, navigateNext, closeLightbox]);

  // Preload adjacent images for better UX
  useEffect(() => {
    if (selectedSection === null || !selectedSectionData) return;
    
    const preloadImage = (url: string) => {
      const img = new Image();
      const optimizedSrc = selectedSectionData
        ? getOptimizedSrc(url, selectedSectionData.title, 1400)
        : url;
      img.src = optimizedSrc;
    };

    // Preload next and previous images
    const nextIndex = (currentIndex + 1) % currentUrls.length;
    const prevIndex = (currentIndex - 1 + currentUrls.length) % currentUrls.length;
    
    if (currentUrls[nextIndex]) preloadImage(currentUrls[nextIndex]);
    if (currentUrls[prevIndex]) preloadImage(currentUrls[prevIndex]);
  }, [selectedSection, currentIndex, currentUrls, selectedSectionData, getOptimizedSrc]);

  // Safari/iOS: addEventListener con passive: false para que preventDefault funcione en touchmove
  useEffect(() => {
    if (selectedSection === null) return;
    const el = touchContainerRef.current;
    if (!el || !handlersRef.current) return;

    const handleTouchStart = (e: TouchEvent) => {
      const t = e.touches[0];
      if (t) {
        swipeStartRef.current = { x: t.clientX, y: t.clientY, time: Date.now() };
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (swipeStartRef.current && e.touches[0]) {
        const t = e.touches[0];
        const deltaX = Math.abs(t.clientX - swipeStartRef.current.x);
        const deltaY = Math.abs(t.clientY - swipeStartRef.current.y);
        if (deltaX > 10 || deltaY > 10) {
          e.preventDefault();
        }
      }
    };

    const handleTouchEnd = (e: TouchEvent) => {
      const h = handlersRef.current;
      if (!swipeStartRef.current || !h || !e.changedTouches[0]) return;
      const t = e.changedTouches[0];
      const dx = t.clientX - swipeStartRef.current.x;
      const dy = t.clientY - swipeStartRef.current.y;
      const dt = Date.now() - swipeStartRef.current.time;
      const vx = Math.abs(dx) / Math.max(dt, 1);
      const vy = Math.abs(dy) / Math.max(dt, 1);
      
      // Ajustado para mejor detección en mobile (Safari iOS y Android)
      const swipeThreshold = h.instagramOpts.isInstagramBrowser ? 15 : 25;
      const velocityThreshold = 0.1;
      const tapThreshold = 12;

      // Swipe vertical (arriba/abajo)
      if (
        Math.abs(dy) > Math.abs(dx) &&
        Math.abs(dy) > swipeThreshold &&
        vy > velocityThreshold
      ) {
        if (dy < 0) h.navigateNext();
        else h.navigatePrev();
      } 
      // Swipe horizontal (izquierda/derecha) - más común en galerías
      else if (
        Math.abs(dx) > Math.abs(dy) &&
        Math.abs(dx) > swipeThreshold &&
        vx > velocityThreshold
      ) {
        if (dx > 0) h.navigatePrev();
        else h.navigateNext();
      } 
      // Tap para cerrar — ignorar el tap que abrió el lightbox (crítico en Instagram IAB)
      else if (Math.abs(dx) < tapThreshold && Math.abs(dy) < tapThreshold && dt < 300) {
        const openedAt = lightboxOpenTimeRef.current;
        if (openedAt && Date.now() - openedAt < 500) return;
        h.closeLightbox();
      }
      swipeStartRef.current = null;
    };

    el.addEventListener("touchstart", handleTouchStart, { passive: true });
    el.addEventListener("touchmove", handleTouchMove, { passive: false });
    el.addEventListener("touchend", handleTouchEnd, { passive: true });

    return () => {
      el.removeEventListener("touchstart", handleTouchStart);
      el.removeEventListener("touchmove", handleTouchMove);
      el.removeEventListener("touchend", handleTouchEnd);
    };
  }, [selectedSection]);

  const handleImageError = (url: string) => {
    setFailedUrls((prev) => new Set(prev).add(url));
  };

  if (isLoading) {
    return (
      <SiteLayout rootClassName={layoutRootClass}>
        <div className="flex-1 flex items-center justify-center py-20">
          <Loader2
            className={cn(
              "w-8 h-8 animate-spin",
              isDarkShell ? "text-white/50" : "text-black/50",
            )}
          />
        </div>
      </SiteLayout>
    );
  }

  if (sections.length === 0) {
    return (
      <SiteLayout rootClassName={layoutRootClass}>
        <div className="flex-1 flex flex-col items-center justify-center px-5 py-24 min-h-[50vh]">
          <h1
            className={cn(
              "text-2xl sm:text-3xl md:text-4xl font-display font-light tracking-tight uppercase mb-4",
              isDarkShell ? "text-white" : "text-black",
            )}
          >
            Portfolio
          </h1>
          <p
            className={cn(
              "font-body text-sm text-center max-w-sm mb-10 leading-relaxed",
              isDarkShell ? "text-white/60" : "text-black/60",
            )}
          >
            {!portfolioPublicEnabled
              ? t("portfolioPage.hidden", "El portfolio no está visible en el sitio en este momento.")
              : t("portfolioPage.empty", "Explora mi trabajo en moda, lifestyle y producción de contenido.")}
          </p>
          <a
            href="/"
            className={cn(
              "font-body text-xs uppercase tracking-[0.2em] transition-colors font-light",
              isDarkShell
                ? "text-white/60 hover:text-white"
                : "text-black/60 hover:text-black",
            )}
          >
            ← Volver al inicio
          </a>
        </div>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout rootClassName={layoutRootClass}>
      <div
        className="mx-auto max-w-7xl py-6 sm:py-10 md:py-14"
        style={{ paddingBottom: "max(1.5rem, env(safe-area-inset-bottom, 0px))" }}
      >
        <header className="text-center mb-8 sm:mb-12 px-4">
          <h1
            className={cn(
              "text-2xl sm:text-3xl md:text-4xl font-display font-light tracking-tight uppercase mb-3",
              isDarkShell ? "text-white" : "text-black",
            )}
          >
            Portfolio
          </h1>
          {sections.length > 1 ? (
            <nav
              className="flex items-center justify-start sm:justify-center gap-x-3 gap-y-1 px-1 overflow-x-auto pb-2 -mx-1 snap-x snap-mandatory scrollbar-none"
              aria-label={t("portfolioPage.sectionsNav", "Secciones del portfolio")}
              style={{ WebkitOverflowScrolling: 'touch' }}
            >
              {sections.map((section, idx) => {
                const anchorId = portfolioSectionAnchorId(section.title);
                return (
                  <a
                    key={section.id ?? anchorId}
                    href={`#${anchorId}`}
                    className={cn(
                      "font-body text-[11px] uppercase tracking-[0.2em] font-light ui-transition whitespace-nowrap snap-start min-h-[44px] inline-flex items-center px-3 py-1 rounded-full border touch-manipulation",
                      isDarkShell
                        ? "text-white/70 hover:text-white border-white/15 hover:border-white/40 active:bg-white/10"
                        : "text-black/70 hover:text-black border-black/15 hover:border-black/40 active:bg-black/5",
                    )}
                  >
                    {section.title}
                  </a>
                );
              })}
            </nav>
          ) : (
            <div className="flex items-center justify-center gap-4">
              <span
                className={cn(
                  "w-12 h-px",
                  isDarkShell ? "bg-white/20" : "bg-black/20",
                )}
              />
              <p
                className={cn(
                  "font-body text-xs uppercase tracking-[0.2em] font-light",
                  isDarkShell ? "text-white/60" : "text-black/60",
                )}
              >
                {t("portfolioPage.collections", "Colecciones")}
              </p>
              <span
                className={cn(
                  "w-12 h-px",
                  isDarkShell ? "bg-white/20" : "bg-black/20",
                )}
              />
            </div>
          )}

          {/* CTA fijo (no depende de site-settings): consultas → formulario Tally. */}
          <div className="mt-6 flex justify-center">
            <ConsultaCtaButton
              variant={isDarkShell ? "dark" : "light"}
              onClick={() =>
                trackBaseTemplateClick({
                  templateName: "BaseTemplate",
                  elementId: "portfolio_cta_consulta",
                  label: "Portfolio CTA consulta",
                  surface: "gallery",
                  href: TALLY_CONTACT_PUBLIC_URL,
                  countryCode: country || undefined,
                  extra: { page: "portfolio" },
                })
              }
            />
          </div>
        </header>

        <div className="space-y-8 sm:space-y-12 md:space-y-16">
          {sections.map((section, sectionIdx) => {
            const entries = section.urls
              .map((raw) => parsePortfolioUrl(raw.trim()))
              .filter(
                (e) =>
                  e.url &&
                  isCloudinaryImage(e.url) &&
                  !failedUrls.has(e.url),
              );
            if (entries.length === 0) return null;

            const anchorId = portfolioSectionAnchorId(section.title);

            return (
              <section
                key={section.id ?? section.title ?? sectionIdx}
                id={anchorId}
                className="animate-fade-in scroll-mt-24 sm:scroll-mt-28"
              >
                <div className="text-center mb-4 sm:mb-6 px-4">
                  <h2
                    className={cn(
                      "text-xl sm:text-2xl md:text-3xl font-display font-light tracking-tight uppercase",
                      isDarkShell ? "text-white" : "text-black",
                    )}
                  >
                    {section.title}
                  </h2>
                  <div
                    className={cn(
                      "w-12 h-px mx-auto mt-3",
                      isDarkShell ? "bg-white/20" : "bg-black/20",
                    )}
                  />
                </div>

                <div
                  className={cn(
                    "portfolio-grid",
                    isInstagram ? instagramOpts.cssClasses.gallery : undefined,
                  )}
                >
                  {entries.map((entry, idx) => (
                    <div
                      key={`${sectionIdx}-${entry.url}-${idx}`}
                      data-photo-track
                      data-photo-src={resolveUrl(entry.url, section.title)}
                      data-photo-position={idx}
                      className={`portfolio-item ${entry.wide ? "portfolio-item-wide" : ""}`}
                    >
                      <button
                        type="button"
                        onClick={() => openLightbox(sectionIdx, idx)}
                        aria-label={`Ver ${section.title} - ${idx + 1}`}
                        className={cn(
                          "h-full w-full min-h-[44px] focus:outline-none focus:ring-2 touch-manipulation",
                          isDarkShell
                            ? "focus:ring-white/35"
                            : "focus:ring-black/30",
                        )}
                      >
                        <img
                          src={getOptimizedSrc(entry.url, section.title)}
                          srcSet={
                            resolveUrl(entry.url, section.title).includes(
                              "res.cloudinary.com",
                            )
                              ? `${getOptimizedSrc(entry.url, section.title, 400)} 400w, ${getOptimizedSrc(entry.url, section.title, 800)} 800w, ${getOptimizedSrc(entry.url, section.title, 1200)} 1200w`
                              : undefined
                          }
                          sizes={entry.wide ? "(max-width: 1023px) 100vw, 66vw" : "(max-width: 639px) 50vw, (max-width: 1023px) 50vw, 33vw"}
                          alt={`${section.title} - ${idx + 1}`}
                          className="w-full h-full object-cover"
                          loading="lazy"
                          decoding="async"
                          onError={(e) => {
                            handleImageError(entry.url);
                            const target = e.target as HTMLImageElement;
                            if (target) target.style.display = "none";
                          }}
                        />
                      </button>
                    </div>
                  ))}
                </div>
              </section>
            );
          })}
        </div>

        <div className="text-center mt-12 sm:mt-16 px-4">
          <a
            href="/"
            className={cn(
              "font-body inline-block text-xs uppercase tracking-[0.2em] transition-colors font-light",
              isDarkShell
                ? "text-white/60 hover:text-white"
                : "text-black/60 hover:text-black",
            )}
          >
            {t("portfolioPage.backHome", "Volver al inicio")}
          </a>
        </div>
      </div>

      {selectedSection !== null && currentUrl && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Visor de imagen del portfolio"
          className={cn(
            "portfolio-lightbox-viewport fixed inset-0 z-50 bg-black/98 select-none",
            isInstagram ? instagramOpts.cssClasses.modal : undefined,
          )}
          style={{
            touchAction: isInstagram ? "none" : "pan-y pan-x",
            WebkitTouchCallout: "none",
            WebkitUserSelect: "none",
            userSelect: "none",
          } as React.CSSProperties}
          onClick={(e) => {
            // Excellence UX: click on backdrop (not on interactive children) closes
            if (e.target === e.currentTarget) {
              closeLightbox();
            }
          }}
        >
          <button
            type="button"
            onClick={closeLightbox}
            className={cn(
              "portfolio-close-button absolute flex h-12 w-12 items-center justify-center rounded-full bg-white/15 text-white shadow-lg focus:outline-none focus:ring-2 focus:ring-white/40 touch-manipulation sm:h-14 sm:w-14 z-30",
              isInstagram ? instagramOpts.cssClasses.buttons : undefined,
            )}
            style={{
              top: "max(1rem, env(safe-area-inset-top, 0px))",
              right: "max(1rem, env(safe-area-inset-right, 0px))",
            }}
            aria-label="Cerrar"
          >
            <X className="w-6 h-6 sm:w-7 sm:h-7" strokeWidth={2.5} />
          </button>

          <div
            role="status"
            aria-live="polite"
            className="font-body absolute left-1/2 -translate-x-1/2 z-30 px-4 py-2 bg-white/20 backdrop-blur-md rounded-full text-white text-xs sm:text-sm font-light tracking-[0.15em] uppercase shadow-lg"
            style={{ top: "max(1rem, env(safe-area-inset-top, 0px))" }}
          >
            {currentIndex + 1} / {currentUrls.length}
          </div>

          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              navigatePrev();
            }}
            onTouchEnd={(e) => e.stopPropagation()}
            className={cn(
              "portfolio-nav-button absolute left-3 top-1/2 flex h-14 w-14 items-center justify-center rounded-full bg-white/25 text-white shadow-lg focus:outline-none focus:ring-2 focus:ring-white/40 touch-manipulation sm:left-6 sm:h-16 sm:w-16 z-30",
              isInstagram ? instagramOpts.cssClasses.buttons : undefined,
            )}
            style={{ transform: 'translateY(-50%)' }}
            aria-label="Anterior"
          >
            <ChevronLeft className="w-7 h-7 sm:w-8 sm:h-8" strokeWidth={2.5} />
          </button>

          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              navigateNext();
            }}
            onTouchEnd={(e) => e.stopPropagation()}
            className={cn(
              "portfolio-nav-button absolute right-3 top-1/2 flex h-14 w-14 items-center justify-center rounded-full bg-white/25 text-white shadow-lg focus:outline-none focus:ring-2 focus:ring-white/40 touch-manipulation sm:right-6 sm:h-16 sm:w-16 z-30",
              isInstagram ? instagramOpts.cssClasses.buttons : undefined,
            )}
            style={{ transform: 'translateY(-50%)' }}
            aria-label="Siguiente"
          >
            <ChevronRight className="w-7 h-7 sm:w-8 sm:h-8" strokeWidth={2.5} />
          </button>

          <div
            ref={touchContainerRef}
            className="portfolio-touch-area relative w-full h-full flex items-center justify-center overflow-hidden z-10"
            style={{ WebkitOverflowScrolling: "touch" }}
          >
            {imageLoading && (
              <div className="absolute inset-0 flex items-center justify-center z-20">
                <Loader2 className="w-10 h-10 animate-spin text-white/60" />
              </div>
            )}
            <div className="relative w-full h-full flex items-center justify-center px-2">
              <img
                key={currentUrl}
                src={
                  selectedSectionData
                    ? getOptimizedSrc(
                        currentUrl!,
                        selectedSectionData.title,
                        1400,
                      )
                    : currentUrl
                }
                alt={`${sections[selectedSection].title} - ${currentIndex + 1}`}
                className="max-w-full max-h-full object-contain"
                style={{
                  maxHeight: "min(85vh, 85dvh)",
                  animation: "portfolioScaleIn 0.3s ease-out",
                  opacity: imageLoading ? 0 : 1,
                  transition: 'opacity 0.2s ease-in-out'
                }}
                onLoad={() => setImageLoading(false)}
                onError={() => setImageLoading(false)}
              />
            </div>
            <div
              className={cn(
                "pointer-events-none absolute bottom-6 left-1/2 flex -translate-x-1/2 flex-col items-center gap-1 rounded-full bg-black/30 px-4 py-2 text-white/60 backdrop-blur-sm md:hidden",
                isInstagram && "bottom-8",
              )}
              style={{ paddingBottom: "calc(1.5rem + env(safe-area-inset-bottom, 0px))" }}
            >
              <span className="text-center font-body text-xs font-light tracking-wide">
                {isInstagram 
                  ? t("portfolioPage.swipeHintIab", "Deslizá para ver más · Tocá afuera para cerrar")
                  : t("portfolioPage.swipeHint", "Desliza · Toca para cerrar")}
              </span>
            </div>
          </div>

          <style>{`
            @keyframes portfolioScaleIn {
              from { 
                opacity: 0.5; 
                transform: scale(0.96) translateZ(0); 
              }
              to { 
                opacity: 1; 
                transform: scale(1) translateZ(0); 
              }
            }
            
            /* Optimize animations for mobile */
            @media (prefers-reduced-motion: reduce) {
              @keyframes portfolioScaleIn {
                from { opacity: 0; }
                to { opacity: 1; }
              }
              .portfolio-item img,
              .portfolio-nav-button,
              .portfolio-close-button {
                transition: none;
              }
            }
          `}</style>
        </div>
      )}
    </SiteLayout>
  );
};

export default Portfolio;
